import React from 'react';
import { X, FilePlus, Upload } from 'lucide-react';
import mammoth from "mammoth";
import JSZip from "jszip";
import * as pdfjsLib from "pdfjs-dist";
import { OPS } from "pdfjs-dist";
import './fileUpload.css';
import jsPDF from 'jspdf';

// Ensure the worker source is set
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const promisifiedPDFGetObject = async (page, index) => {
  return new Promise((resolve, reject) => {
    page.objs.get(index, (resolved) => {
      if (resolved) {
        resolve(resolved);
      } else {
        reject(new Error("Error getting object"));
      }
    });
  });
};

// unzipper
async function expandZipToFiles(zipBlob) {
  const zip = await JSZip.loadAsync(zipBlob);
  const out = [];
  for (const [name, entry] of Object.entries(zip.files)) {
    if (entry.dir) continue;
    const lower = name.toLowerCase();
    if (!/\.(docx|pptx|pdf)$/.test(lower)) continue;

    const ab = await entry.async('arraybuffer');
    const baseName = name.split('/').pop();
    let type = 'application/octet-stream';
    if (lower.endsWith('.docx')) type = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    else if (lower.endsWith('.pptx')) type = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    else if (lower.endsWith('.pdf')) type = 'application/pdf';

    out.push(new File([ab], baseName, { type }));
  }
  return out;
}

// const convertImageToBase64 = (file) => {
//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onload = () => resolve(reader.result.split(",")[1]); // Extract Base64 string
//     reader.onerror = (error) => reject(error);
//     reader.readAsDataURL(file);
//   });
// };

const MAX_DIMENSION = 2000;

const MAX_TOTAL_UPLOAD_BYTES = 32 * 1024 * 1024;

const convertImageToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;

  reader.onload = (e) => {
    const img = new Image();
    img.onerror = reject;
    img.decoding = 'async';
    img.src = e.target.result;

    img.onload = () => {
      let { width, height } = img;

      // scale if longest side > MAX_DIMENSION
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }

      // draw
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // choose an output mime that preserves transparency when needed
      const ext = (file.type || '').toLowerCase();
      const supportsAlpha = ext.includes('png') || ext.includes('webp') || ext.includes('gif');
      const outMime = supportsAlpha ? (ext || 'image/png') : 'image/jpeg';
      // quality only used by jpeg/webp
      const dataUrl = canvas.toDataURL(outMime, 0.9);

      // return base64-only (matches how you build data URLs elsewhere)
      resolve(dataUrl.split(',')[1]);
    };
  };

  reader.readAsDataURL(file);
});



const extractPptxContent = async (arrayBuffer) => {
  try {
    const zip = await JSZip.loadAsync(arrayBuffer);
    let slidesText = [];

    const filesToParse = Object.keys(zip.files).filter(name =>
      (name.startsWith("ppt/slides/slide") || name.startsWith("ppt/notesSlides/notesSlide")) &&
      name.endsWith(".xml")
    );

    for (const file of filesToParse) {
      const content = await zip.files[file].async("text");
      const textMatches = content.match(/<a:t[^>]*>([^<]*)<\/a:t>/g);
      if (textMatches) {
        const slideText = textMatches
          .map(match => match.replace(/<[^>]*>/g, "").trim())
          .filter(line => line.length > 0)
          .join(" ");

        if (slideText.length > 0) {
          slidesText.push(slideText);
        }
      }
    }

    return slidesText
      .map((slide, index) => `--- Slide ${index + 1} ---\n${slide.trim()}`)
      .join("\n\n");

  } catch (error) {
    console.error("Error extracting PPTX content:", error);
    return "";
  }
};

const extractPdfContent = async (arrayBuffer) => {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let text = "";
  const images = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();

    text += textContent.items.map((item) => item.str).join(" ") + "\n";

    const operatorList = await page.getOperatorList();

    for (let j = 0; j < operatorList.fnArray.length; j++) {
      const fn = operatorList.fnArray[j];

      if (fn === OPS.paintImageXObject || fn === OPS.paintJpegXObject) {
        const imgArgs = operatorList.argsArray[j];
        const imageKey = imgArgs[0];

        try {
          const image = await promisifiedPDFGetObject(page, imageKey);

          if (image && (image.data || image.bitmap)) {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");

            canvas.width = image.width;
            canvas.height = image.height;

            if (image.data) {
              let imgData;
              if (image.data.length === image.width * image.height * 3) {
                const rgbaData = new Uint8ClampedArray(image.width * image.height * 4);
                for (let k = 0, l = 0; k < image.data.length; k += 3, l += 4) {
                  rgbaData[l] = image.data[k];
                  rgbaData[l + 1] = image.data[k + 1];
                  rgbaData[l + 2] = image.data[k + 2];
                  rgbaData[l + 3] = 255;
                }
                imgData = context.createImageData(image.width, image.height);
                imgData.data.set(rgbaData);
              } else if (image.data.length === image.width * image.height * 4) {
                imgData = context.createImageData(image.width, image.height);
                imgData.data.set(image.data);
              } else {
                // console.warn("Unsupported image format or missing data.");
                continue;
              }

              context.putImageData(imgData, 0, 0);
            } else if (image.bitmap) {
              context.drawImage(image.bitmap, 0, 0, image.width, image.height);
            }

            const dataURL = canvas.toDataURL();
            text += `\n[Image extracted from PDF on page ${i}]\n`;

            images.push({
              type: "input_image",
              image_url: dataURL,
            });
          } else {
            // console.warn("Image data is missing or invalid for key:", imageKey);
          }
        } catch (error) {
          // console.warn("Error extracting image:", error);
        }
      }
    }
  }

  return { text, images };
};

const convertPptxToPdf = async (file, shouldDownload = false) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Get slide dimensions from presentation.xml
    const presentationXml = await zip.files["ppt/presentation.xml"]?.async("text");
    let slideWidth = 720;
    let slideHeight = 540;

    if (presentationXml) {
      const sizeMatch = presentationXml.match(/cx="(\d+)".*?cy="(\d+)"/);
      if (sizeMatch) {
        slideWidth = parseInt(sizeMatch[1]) / 12700; // Convert EMU to points
        slideHeight = parseInt(sizeMatch[2]) / 12700;
      }
    }

    // Create PDF with slide dimensions
    const pdf = new jsPDF({
      orientation: slideWidth > slideHeight ? 'landscape' : 'portrait',
      unit: 'pt',
      format: [slideWidth, slideHeight]
    });

    // Get all slide files and sort them
    const slideFiles = Object.keys(zip.files)
      .filter(name => name.startsWith("ppt/slides/slide") && name.endsWith(".xml"))
      .sort((a, b) => {
        const numA = parseInt(a.match(/slide(\d+)\.xml/)?.[1] || 0);
        const numB = parseInt(b.match(/slide(\d+)\.xml/)?.[1] || 0);
        return numA - numB;
      });

    let isFirstPage = true;

    for (const slideFile of slideFiles) {
      if (!isFirstPage) {
        pdf.addPage([slideWidth, slideHeight]);
      }
      isFirstPage = false;

      const slideNumber = slideFile.match(/slide(\d+)\.xml/)?.[1];
      // console.log(`Processing slide ${slideNumber}`);

      // Parse slide XML
      const slideXml = await zip.files[slideFile].async("text");

      // Extract text with positioning information
      await processSlideText(pdf, slideXml, slideWidth, slideHeight);

      // Extract and position images from this specific slide
      await processImages({pdf, zip, slideXml, slidePath: slideFile, slideNumber, slideWidth, slideHeight});

      // Speaker notes
      const notesPath = `ppt/notesSlides/notesSlide${slideNumber}.xml`;
      if (zip.files[notesPath]) {
        const notesXml = await zip.files[notesPath].async("text");
        const notesTextMatches = notesXml.match(/<a:t[^>]*>([^<]*)<\/a:t>/g) || [];
        const notesText = notesTextMatches
          .map(match => match.replace(/<[^>]*>/g, "").trim())
          .filter(line => line.length > 0)
          .join(" ");
        if (notesText.length > 0) {
          // Render notes below slide content
          pdf.setFontSize(10);
          pdf.setTextColor(100, 100, 100);
          const notesY = slideHeight - 40;
          pdf.text(
            pdf.splitTextToSize(`SpeakerNotes: ${notesText}`, slideWidth - 40),
            20,
            notesY
          );
          pdf.setTextColor(0, 0, 0);
        }
      }
    }

    // If download is requested, save the PDF
    if (shouldDownload) {
      const pdfFileName = file.name.replace(/\.pptx$/i, '_converted.pdf');
      console.log("Triggering PDF download:", pdfFileName);
      pdf.save(pdfFileName);
    }

    // Convert PDF to blob for upload
    const pdfBlob = pdf.output('blob');

    // Create a new File object with PDF data
    const pdfFile = new File([pdfBlob], file.name.replace(/\.pptx$/i, '.pdf'), {
      type: 'application/pdf'
    });

    return pdfFile;

  } catch (error) {
    console.error("Error converting PPTX to PDF:", error);
    throw new Error(`Failed to convert ${file.name} to PDF: ${error.message}`);
  }
};

const mergeWithoutDupes = (existing, toAdd) => {
  const seen = new Set(existing.map(f => f.name));
  return [...existing, ...toAdd.filter(f => !seen.has(f.name))];
};

// Helper function to process images with exact positioning
const processImages = async ({ pdf, zip, slideXml, slidePath, slideNumber, slideWidth, slideHeight }) => {
  const EMU_PER_POINT = 12700;
  const emuToPt = (emu) => emu / EMU_PER_POINT;
  
  // Add memory management and performance optimizations
  const MAX_IMAGES_PER_SLIDE = 20; // Limit to prevent memory issues
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB limit per image
  const SUPPORTED_FORMATS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'];

  const resolveRelTarget = (slidePath, target) => {
    let t = target.replace(/\\/g, "/");
    if (t.startsWith("/")) t = t.slice(1);
    if (t.startsWith("ppt/")) return t;
    const baseParts = slidePath.split("/");
    baseParts.pop();
    const targetParts = t.split("/");
    for (const part of targetParts) {
      if (part === "..") baseParts.pop();
      else if (part === ".") continue;
      else baseParts.push(part);
    }
    return baseParts.join("/");
  };

  const uint8ToBase64 = (u8) => {
    let binary = "";
    const len = u8.length;
    for (let i = 0; i < len; i++) binary += String.fromCharCode(u8[i]);
    return btoa(binary);
  };

  const guessImageFormatForJsPDF = (ext) => {
    const e = ext.toLowerCase();
    if (e === "jpg" || e === "jpeg") return "JPEG";
    if (e === "png") return "PNG";
    if (e === "gif") return "GIF";
    if (e === "bmp") return "BMP";
    if (e === "webp") return "WEBP";
    return "PNG";
  };

  // Optimized image conversion with size limits
  const convertBytesToPngBase64 = async (uint8, mime, maxSize = 1024) => {
    try {
      if (uint8.length > MAX_IMAGE_SIZE) {
        // console.warn(`Image too large (${uint8.length} bytes), skipping conversion`);
        throw new Error('Image too large');
      }

      const blob = new Blob([uint8], { type: mime });
      const bmp = await createImageBitmap(blob);
      
      // Calculate scaled dimensions to keep within reasonable limits
      let { width, height } = bmp;
      const maxDimension = maxSize;
      
      if (width > maxDimension || height > maxDimension) {
        const scale = Math.min(maxDimension / width, maxDimension / height);
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }

      const canvas = (typeof OffscreenCanvas !== "undefined")
        ? new OffscreenCanvas(width, height)
        : (() => { const cv = document.createElement("canvas"); cv.width = width; cv.height = height; return cv; })();
      
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bmp, 0, 0, width, height);
      
      let pngBlob;
      if (canvas.convertToBlob) {
        pngBlob = await canvas.convertToBlob({ type: "image/png", quality: 0.8 });
      } else {
        const dataURL = canvas.toDataURL("image/png", 0.8);
        const bstr = atob(dataURL.split(",")[1]);
        const buf = new Uint8Array(bstr.length);
        for (let i = 0; i < bstr.length; i++) buf[i] = bstr.charCodeAt(i);
        pngBlob = new Blob([buf], { type: "image/png" });
      }
      
      const pngU8 = new Uint8Array(await pngBlob.arrayBuffer());
      bmp.close?.(); // Clean up bitmap if possible
      
      return `data:image/png;base64,${uint8ToBase64(pngU8)}`;
    } catch (error) {
      console.warn('Image conversion failed:', error);
      throw error;
    }
  };
  // Enhanced format support including WMF/EMF (convert to PNG)
  const convertUnsupportedFormat = async (uint8, ext) => {
    try {
      
      // Try different MIME types for better compatibility
      const mimeTypes = [
        `image/${ext}`,
        'application/octet-stream',
        'image/x-wmf',
        'image/x-emf'
      ];
      
      for (const mimeType of mimeTypes) {
        try {
          const testBlob = new Blob([uint8], { type: mimeType });
          const bmp = await createImageBitmap(testBlob);
          
          const canvas = document.createElement('canvas');
          canvas.width = bmp.width;
          canvas.height = bmp.height;
          const ctx = canvas.getContext('2d');
          
          // Fill with white background
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(bmp, 0, 0);
          
          bmp.close?.();
          return canvas.toDataURL('image/png');
        } catch (e) {
          continue; // Try next MIME type
        }
      }
      
      throw new Error(`Unsupported format: ${ext}`);
    } catch (error) {
      console.warn(`Failed to convert ${ext} format:`, error);
      return null;
    }
  };

  // Enhanced SVG converter with better error handling
  const convertSvgToPng = async (svgUint8) => {
    try {
      if (svgUint8.length > MAX_IMAGE_SIZE) {
        console.warn('SVG file too large, skipping');
        return null;
      }

      const svgText = new TextDecoder().decode(svgUint8);
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      return new Promise((resolve, reject) => {
        const img = new Image();
        const timeout = setTimeout(() => {
          URL.revokeObjectURL(svgUrl);
          reject(new Error('SVG loading timeout'));
        }, 10000); // 10 second timeout
        
        img.onload = () => {
          clearTimeout(timeout);
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Limit SVG size to prevent memory issues
            const maxSize = 800;
            let { naturalWidth: width, naturalHeight: height } = img;
            
            if (width > maxSize || height > maxSize) {
              const scale = Math.min(maxSize / width, maxSize / height);
              width = Math.floor(width * scale);
              height = Math.floor(height * scale);
            }
            
            canvas.width = width || 200;
            canvas.height = height || 200;
            
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            const pngDataUrl = canvas.toDataURL('image/png', 0.8);
            URL.revokeObjectURL(svgUrl);
            resolve(pngDataUrl);
          } catch (error) {
            URL.revokeObjectURL(svgUrl);
            reject(error);
          }
        };
        
        img.onerror = () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(svgUrl);
          reject(new Error('Failed to load SVG image'));
        };
        
        img.src = svgUrl;
      });
    } catch (error) {
      console.warn('SVG conversion failed:', error);
      return null;
    }
  };

  // Optimized image processing with memory management
  const addImageToPdf = async (mediaPath, xPt, yPt, wPt, hPt) => {
    try {
      let drawX = Math.max(0, Math.min(xPt, slideWidth - 10));
      let drawY = Math.max(0, Math.min(yPt, slideHeight - 10));
      let drawW = Math.min(wPt, slideWidth - drawX);
      let drawH = Math.min(hPt, slideHeight - drawY);

      if (drawW <= 10 || drawH <= 10) {
        // console.warn(`Slide ${slideNumber}: Image ${mediaPath} has invalid dimensions, using defaults`);
        drawW = Math.min(200, slideWidth * 0.3);
        drawH = Math.min(150, slideHeight * 0.3);
      }

      const imgFile = zip.file(mediaPath);
      if (!imgFile) {
        // console.warn(`Slide ${slideNumber}: Image file not found: ${mediaPath}`);
        return false;
      }

      // Check file size before processing
      const compressedSize = imgFile._data?.compressedSize || 0;
      if (compressedSize > MAX_IMAGE_SIZE) {
        // console.warn(`Slide ${slideNumber}: Image ${mediaPath} too large (${compressedSize} bytes), skipping`);
        return false;
      }

      const uint8 = await imgFile.async("uint8array");
      const ext = mediaPath.split(".").pop()?.toLowerCase() || "";
      
      // Skip unsupported formats early
      if (!SUPPORTED_FORMATS.includes(ext) && !['wmf', 'emf'].includes(ext)) {
        console.warn(`Slide ${slideNumber}: Unsupported image format: ${ext}`);
        return false;
      }

      let dataUrl, jsPdfFormat;

      // Handle different image formats with optimization
      if (ext === 'svg') {
        // console.log(`Slide ${slideNumber}: Converting SVG to PNG: ${mediaPath}`);
        dataUrl = await convertSvgToPng(uint8);
        if (!dataUrl) return false;
        jsPdfFormat = "PNG";
      } else if (['wmf', 'emf'].includes(ext)) {
        // console.log(`Slide ${slideNumber}: Converting ${ext.toUpperCase()} to PNG: ${mediaPath}`);
        dataUrl = await convertUnsupportedFormat(uint8, ext);
        if (!dataUrl) return false;
        jsPdfFormat = "PNG";
      } else {
        const base64 = uint8ToBase64(uint8);
        const mime = ext === 'jpg' ? 'jpeg' : ext;
        dataUrl = `data:image/${mime};base64,${base64}`;
        jsPdfFormat = guessImageFormatForJsPDF(ext);
      }

      // Try to add the image with timeout
      try {
        const addImagePromise = new Promise((resolve, reject) => {
          try {
            pdf.addImage(dataUrl, jsPdfFormat, drawX, drawY, drawW, drawH);
            resolve(true);
          } catch (error) {
            reject(error);
          }
        });

        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Image processing timeout')), 15000);
        });

        await Promise.race([addImagePromise, timeoutPromise]);
        // console.log(`✅ Successfully added image ${mediaPath} to slide ${slideNumber} at (${drawX}, ${drawY}) size (${drawW}x${drawH})`);
        return true;
      } catch (err) {
        console.warn(`Slide ${slideNumber}: Direct addImage failed for ${mediaPath}, trying PNG conversion:`, err.message);
        try {
          const pngDataUrl = await convertBytesToPngBase64(uint8, `image/${ext === 'jpg' ? 'jpeg' : ext}`, 512);
          pdf.addImage(pngDataUrl, "PNG", drawX, drawY, drawW, drawH);
          // console.log(`✅ Successfully added image ${mediaPath} to slide ${slideNumber} using PNG fallback`);
          return true;
        } catch (err2) {
          console.error(`Slide ${slideNumber}: PNG fallback also failed for ${mediaPath}:`, err2.message);
          return false;
        }
      }
    } catch (error) {
      // console.error(`Error adding image ${mediaPath} to PDF:`, error.message);
      return false;
    }
  };

  try {
    // console.log(`\n=== Processing slide ${slideNumber} for inserted pictures ===`);
    
    // Get available media files with filtering
    const allMediaFiles = Object.keys(zip.files).filter(name => {
      if (!name.startsWith('ppt/media/')) return false;
      const ext = name.split('.').pop()?.toLowerCase();
      return SUPPORTED_FORMATS.includes(ext) || ['wmf', 'emf'].includes(ext);
    });
    
    // Parse relationships
    let rMap = {};
    const relPath = `ppt/slides/_rels/slide${slideNumber}.xml.rels`;
    const relXml = await zip.file(relPath)?.async("text");
    
    if (relXml) {
      // console.log(`Found slide relationships file: ${relPath}`);
      
      const relRegex = /<Relationship[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"[^>]*(?:Type="([^"]+)")?[^>]*\/?>/gi;
      let relMatch;
      while ((relMatch = relRegex.exec(relXml)) !== null) {
        const rId = relMatch[1];
        const target = relMatch[2];
        
        const absPath = resolveRelTarget(slidePath, target);
        rMap[rId] = absPath;
      }
    }

    // Fallback mapping if needed
    if (Object.keys(rMap).length === 0) {
      
      const embedRegex = /r:embed="([^"]+)"/g;
      const embedMatches = [];
      let embedMatch;
      while ((embedMatch = embedRegex.exec(slideXml)) !== null) {
        if (!embedMatches.includes(embedMatch[1])) {
          embedMatches.push(embedMatch[1]);
        }
      }
      
      const sortedMediaFiles = allMediaFiles.sort((a, b) => {
        const aNum = parseInt(a.match(/image(\d+)/)?.[1] || '0');
        const bNum = parseInt(b.match(/image(\d+)/)?.[1] || '0');
        return aNum - bNum;
      });
      
      embedMatches.slice(0, MAX_IMAGES_PER_SLIDE).forEach((rId, index) => {
        if (index < sortedMediaFiles.length) {
          rMap[rId] = sortedMediaFiles[index];
        }
      });
    }

    if (Object.keys(rMap).length === 0) {
      // console.log(`Slide ${slideNumber}: No image relationships found`);
      return;
    }

    // Process images with limits
    let totalImagesProcessed = 0;
    let skippedImages = 0;

    // Process <p:pic> elements
    const picRegex = /<p:pic[\s\S]*?<\/p:pic>/g;
    const picEls = slideXml.match(picRegex) || [];
    // console.log(`Slide ${slideNumber}: Found ${picEls.length} <p:pic> elements`);

    for (const picXml of picEls) {
      if (totalImagesProcessed >= MAX_IMAGES_PER_SLIDE) {
        skippedImages++;
        continue;
      }

      try {
        const embedMatch = picXml.match(/<a:blip[^>]*r:embed="([^"]+)"/);
        if (!embedMatch) continue;
        
        const rId = embedMatch[1];
        const mediaPath = rMap[rId];
        if (!mediaPath) continue;

        const xfrmMatch = picXml.match(/<a:xfrm[^>]*>([\s\S]*?)<\/a:xfrm>/);
        let xPt = 50, yPt = 50, wPt = 200, hPt = 150;
        if (xfrmMatch) {
          const offMatch = xfrmMatch[1].match(/<a:off[^>]*x="([^"]+)"[^>]*y="([^"]+)"/);
          const extMatch = xfrmMatch[1].match(/<a:ext[^>]*cx="([^"]+)"[^>]*cy="([^"]+)"/);
          if (offMatch) {
            xPt = emuToPt(parseInt(offMatch[1], 10));
            yPt = emuToPt(parseInt(offMatch[2], 10));
          }
          if (extMatch) {
            wPt = emuToPt(parseInt(extMatch[1], 10));
            hPt = emuToPt(parseInt(extMatch[2], 10));
          }
        }

        const success = await addImageToPdf(mediaPath, xPt, yPt, wPt, hPt);
        if (success) totalImagesProcessed++;
      } catch (error) {
        console.error(`Error processing <p:pic> image:`, error.message);
      }
    }

    // Process <p:sp> elements with image fills
    const spRegex = /<p:sp[\s\S]*?<\/p:sp>/g;
    const spEls = slideXml.match(spRegex) || [];
    // console.log(`Slide ${slideNumber}: Found ${spEls.length} <p:sp> elements`);

    for (const spXml of spEls) {
      if (totalImagesProcessed >= MAX_IMAGES_PER_SLIDE) {
        skippedImages++;
        continue;
      }

      try {
        const blipFillMatch = spXml.match(/<a:blipFill[\s\S]*?<\/a:blipFill>/);
        if (!blipFillMatch) continue;

        const blipMatch = blipFillMatch[0].match(/<a:blip[^>]*r:embed="([^"]+)"/);
        if (!blipMatch) continue;

        const rId = blipMatch[1];
        const mediaPath = rMap[rId];
        if (!mediaPath) continue;

        const spPrMatch = spXml.match(/<p:spPr[\s\S]*?<\/p:spPr>/);
        let xPt = 50, yPt = 50 + (totalImagesProcessed * 160), wPt = 200, hPt = 150;
        
        if (spPrMatch) {
          const xfrmMatch = spPrMatch[0].match(/<a:xfrm[^>]*>([\s\S]*?)<\/a:xfrm>/);
          if (xfrmMatch) {
            const offMatch = xfrmMatch[1].match(/<a:off[^>]*x="([^"]+)"[^>]*y="([^"]+)"/);
            const extMatch = xfrmMatch[1].match(/<a:ext[^>]*cx="([^"]+)"[^>]*cy="([^"]+)"/);
            
            if (offMatch) {
              xPt = emuToPt(parseInt(offMatch[1], 10));
              yPt = emuToPt(parseInt(offMatch[2], 10));
            }
            if (extMatch) {
              wPt = emuToPt(parseInt(extMatch[1], 10));
              hPt = emuToPt(parseInt(extMatch[2], 10));
            }
          }
        }

        const success = await addImageToPdf(mediaPath, xPt, yPt, wPt, hPt);
        if (success) totalImagesProcessed++;
      } catch (error) {
        // console.error(`Error processing <p:sp> image fill:`, error.message);
      }
    }

    // console.log(totalImagesProcessed > 0 
    //   ? `✅ Slide ${slideNumber}: Successfully processed ${totalImagesProcessed} images` +
    //     (skippedImages > 0 ? ` (${skippedImages} skipped due to limits)` : '')
    //   : `Slide ${slideNumber}: No images were successfully processed`);
    

    // Force garbage collection hint
    if (typeof window !== 'undefined' && window.gc) {
      window.gc();
    }

  } catch (err) {
    console.error(`Slide ${slideNumber}: processImages crashed:`, err.message);
  }
};


const processSlideText = async (pdf, slideXml, slideWidth, slideHeight) => {
  try {
    // Parse text boxes with positioning
    const textBoxRegex = /<p:sp[^>]*>[\s\S]*?<\/p:sp>/g;
    const textBoxes = slideXml.match(textBoxRegex) || [];
    
    for (const textBox of textBoxes) {
      // Extract position and size information
      const transformMatch = textBox.match(/<a:xfrm[^>]*>([\s\S]*?)<\/a:xfrm>/);
      let x = 50, y = 50, width = 200, height = 100; // defaults
      
      if (transformMatch) {
        const offsetMatch = transformMatch[1].match(/<a:off[^>]*x="([^"]*)"[^>]*y="([^"]*)"/);
        const extentMatch = transformMatch[1].match(/<a:ext[^>]*cx="([^"]*)"[^>]*cy="([^"]*)"/);
        
        if (offsetMatch) {
          x = parseInt(offsetMatch[1]) / 12700; // Convert EMU to points
          y = parseInt(offsetMatch[2]) / 12700;
        }
        
        if (extentMatch) {
          width = parseInt(extentMatch[1]) / 12700;
          height = parseInt(extentMatch[2]) / 12700;
        }
      }
      
      // Extract text content
      const textMatches = textBox.match(/<a:t[^>]*>([^<]*)<\/a:t>/g) || [];
      
      if (textMatches.length > 0) {
        let currentY = y + 20; // Start with some padding from top
        
        textMatches.forEach((textMatch, index) => {
          const text = textMatch.replace(/<[^>]*>/g, "").trim();
          if (text) {
            // Determine font size based on text hierarchy
            const isTitle = index === 0 && textMatches.length > 1;
            const fontSize = isTitle ? Math.min(24, height / 5) : Math.min(12, height / 8);
            
            pdf.setFontSize(fontSize);
            pdf.setFont(undefined, isTitle ? 'bold' : 'normal');
            
            // Split text to fit within the text box width
            const splitText = pdf.splitTextToSize(text, width - 20);
            
            // Check if text fits within the text box height
            const textHeight = splitText.length * fontSize * 1.2;
            if (currentY + textHeight <= y + height) {
              pdf.text(splitText, x + 10, currentY);
              currentY += textHeight + 5;
            }
          }
        });
      }
    }
  } catch (error) {
    console.warn("Error processing slide text:", error);
  }
};

/** Convert DOCX File -> PDF File. */
const convertDocxToPdf = async (file, shouldDownload = false) => {
  let arrayBuffer, zip;
  try {
    arrayBuffer = await file.arrayBuffer();
    zip = await JSZip.loadAsync(arrayBuffer);
  } catch (err) {
    console.error("DOCX unzip failed:", err);
    throw new Error(`Failed to read ${file.name}: ${err.message}`);
  }

  // Plaintext fallback (mammoth)
  let fallbackText = "";
  try {
    const mammothRes = await mammoth.extractRawText({ arrayBuffer });
    fallbackText = mammothRes.value || "";
  } catch (err) {
    console.warn("Mammoth raw text failed:", err);
  }

  // Create PDF
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'   // 612 x 792
  });
  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const contentWidth = pageWidth - margin * 2;
  const defaultLineH = 16;

  try {
    // Core OOXML parts
    const documentXmlTxt = await zip.file('word/document.xml')?.async('text');
    if (!documentXmlTxt) throw new Error("Missing word/document.xml");

    const documentXml = parseXml(documentXmlTxt);
    if (!documentXml) throw new Error("Unable to parse document.xml");

    // Relationships (images)
    const relMap = await buildDocxImageRelMap(zip);

    // Styles (heading detection, default formatting)
    const styles = await parseDocxStyles(zip);

    // Numbering (bullets / decimals)
    const numbering = await parseDocxNumbering(zip);

    // Render body
    await renderWordBody({
      pdf,
      zip,
      docXml: documentXml,
      relMap,
      styles,
      numbering,
      pageW: pageWidth,
      pageH: pageHeight,
      margin,
      contentW: contentWidth,
      defaultLineH
    });

  } catch (err) {
    console.warn("Structured DOCX render failed; falling back to plaintext.", err);
    await processSimpleText(pdf, fallbackText, margin, contentWidth, pageWidth, pageHeight);
  }

  // Save locally (optional)
  if (shouldDownload) {
    const pdfFileName = file.name.replace(/\.docx$/i, '_converted.pdf');
    pdf.save(pdfFileName);
  }

  // Return File object
  const pdfBlob = pdf.output('blob');
  return new File([pdfBlob], file.name.replace(/\.docx$/i, '.pdf'), {
    type: 'application/pdf'
  });
};

/* ---------- DOCX helpers ---------- */

/** Build rId->imagePath map from word/_rels/document.xml.rels */
async function buildDocxImageRelMap(zip) {
  const relTxt = await zip.file('word/_rels/document.xml.rels')?.async('text');
  const map = {};
  if (!relTxt) return map;

  const relDoc = parseXml(relTxt);
  if (!relDoc) return map;

  const relEls = relDoc.getElementsByTagNameNS('*', 'Relationship');
  for (const el of Array.from(relEls)) {
    const id   = getAttr(el, 'Id');
    const type = getAttr(el, 'Type') || "";
    if (!/image/i.test(type)) continue;
    const tgt  = getAttr(el, 'Target');
    const abs  = resolveOOXMLTarget('word/document.xml', tgt);
    if (id && abs) map[id] = abs;
  }
  return map;
}

/** Parse styles.xml -> {styleId:{type,name,isHeading,bold,italic,sizePt}} */
async function parseDocxStyles(zip) {
  const txt = await zip.file('word/styles.xml')?.async('text');
  const out = {};
  if (!txt) return out;
  const doc = parseXml(txt);
  if (!doc) return out;

  const styleEls = doc.getElementsByTagNameNS('*','style');
  for (const sEl of Array.from(styleEls)) {
    const sid  = getAttr(sEl, 'styleId');
    if (!sid) continue;
    const type = getAttr(sEl, 'type') || "";
    const nameEl = sEl.getElementsByTagNameNS('*','name')[0];
    const name = nameEl ? getAttr(nameEl,'val') : sid;

    const rPr = sEl.getElementsByTagNameNS('*','rPr')[0];
    let fontSz=null, bold=false, italic=false;
    if (rPr) {
      const szEl = rPr.getElementsByTagNameNS('*','sz')[0];
      if (szEl) {
        // Word stores half-points
        fontSz = (parseInt(getAttr(szEl,'val')||"24",10)/2)|0;
      }
      if (rPr.getElementsByTagNameNS('*','b')[0]) bold=true;
      if (rPr.getElementsByTagNameNS('*','i')[0]) italic=true;
    }
    const isHeading = /^Heading\d+$/i.test(name) || /^Title$/i.test(name);
    out[sid] = {type,name,isHeading,bold,italic,sizePt:fontSz};
  }
  return out;
}

/** Parse numbering.xml -> bullet/decimal formats */
async function parseDocxNumbering(zip) {
  const txt = await zip.file('word/numbering.xml')?.async('text');
  if (!txt) return {};
  const doc = parseXml(txt);
  if (!doc) return {};

  // abstractNum -> lvl definitions
  const abstractEls = doc.getElementsByTagNameNS('*','abstractNum');
  const abstractMap = {};
  for (const aEl of Array.from(abstractEls)) {
    const absId = getAttr(aEl, 'abstractNumId');
    const lvlEls = aEl.getElementsByTagNameNS('*','lvl');
    const lvls = {};
    for (const lvlEl of Array.from(lvlEls)) {
      const ilvl = getAttr(lvlEl, 'ilvl') || "0";
      const numFmtEl = lvlEl.getElementsByTagNameNS('*','numFmt')[0];
      const numFmt = numFmtEl ? getAttr(numFmtEl,'val') : "bullet";
      const lvlTextEl = lvlEl.getElementsByTagNameNS('*','lvlText')[0];
      const lvlText = lvlTextEl ? getAttr(lvlTextEl,'val') : "";
      lvls[ilvl] = {numFmt, lvlText};
    }
    abstractMap[absId] = lvls;
  }

  // num instances -> abstract ref
  const numEls = doc.getElementsByTagNameNS('*','num');
  const instMap = {};
  for (const nEl of Array.from(numEls)) {
    const numId = getAttr(nEl, 'numId');
    const absRefEl = nEl.getElementsByTagNameNS('*','abstractNumId')[0];
    const absRef   = absRefEl ? getAttr(absRefEl,'val') : null;
    instMap[numId] = absRef;
  }

  return {abstract:abstractMap, instances:instMap};
}

/**
 * Render Word document <w:body> into pdf.
 * Paragraphs, lists, basic formatting, images.
 */
async function renderWordBody({
  pdf, zip, docXml, relMap, styles, numbering,
  pageW, pageH, margin, contentW, defaultLineH
}) {
  const body = docXml.getElementsByTagNameNS('*','body')[0];
  if (!body) return;

  let y = margin + defaultLineH;

  const childNodes = Array.from(body.childNodes).filter(n=>n.nodeType===1); // elements
  for (const el of childNodes) {
    const local = el.localName;

    // Table? (very simple text grid)
    if (local === 'tbl') {
      y = await renderWordTable(pdf, el, {
        pageW,pageH,margin,contentW,y
      });
      continue;
    }

    // Paragraph
    if (local === 'p') {
      const {needed, draw} = await wordParagraph({
        pdf, pEl:el, styles, numbering, relMap, zip, contentW, defaultLineH
      });

      // new page if needed
      if (y + needed > pageH - margin) {
        pdf.addPage(); y = margin + defaultLineH;
      }

      // draw
      y = await draw(pdf, margin, y, pageW, pageH);

      // small gap
      y += 2;
      continue;
    }

    // Section break (sectPr) -> page break
    if (local === 'sectPr') {
      pdf.addPage(); y = margin + defaultLineH;
      continue;
    }
  }
}


async function wordParagraph({
  pdf, pEl, styles, numbering, relMap, zip, contentW, defaultLineH
}) {
  // pPr
  const pPr = pEl.getElementsByTagNameNS('*','pPr')[0];
  let pStyleId=null;
  if (pPr) {
    const pStyleEl = pPr.getElementsByTagNameNS('*','pStyle')[0];
    if (pStyleEl) pStyleId = getAttr(pStyleEl,'val');
  }
  const styleObj = pStyleId ? styles[pStyleId] : null;
  const isHeading = styleObj?.isHeading || false;
  const baseFontSize = styleObj?.sizePt || (isHeading?16:12);

  // numbering
  let bulletPrefix="";
  if (pPr) {
    const numPrEl = pPr.getElementsByTagNameNS('*','numPr')[0];
    if (numPrEl) {
      const numIdEl = numPrEl.getElementsByTagNameNS('*','numId')[0];
      const ilvlEl  = numPrEl.getElementsByTagNameNS('*','ilvl')[0];
      if (numIdEl) {
        const numId = getAttr(numIdEl,'val');
        const lvl   = getAttr(ilvlEl,'val') || "0";
        const absRef = numbering?.instances?.[numId];
        const lvlInfo = numbering?.abstract?.[absRef]?.[lvl];
        if (lvlInfo) {
          if (lvlInfo.numFmt === 'bullet') bulletPrefix = "• ";
          else if (lvlInfo.numFmt === 'decimal') bulletPrefix = "1. ";
          else if (lvlInfo.lvlText) bulletPrefix = lvlInfo.lvlText.replace(/%[0-9]/g,"1")+" ";
        }
      }
    }
  }

  // gather runs text
  const rEls = pEl.getElementsByTagNameNS('*','r');
  const textRuns=[];
  let combinedTxt="";
  for (const rEl of Array.from(rEls)) {
    const tEl = rEl.getElementsByTagNameNS('*','t')[0];
    if (!tEl) continue;
    const txt = tEl.textContent || "";
    const rPr = rEl.getElementsByTagNameNS('*','rPr')[0];
    const bold = !!(rPr && rPr.getElementsByTagNameNS('*','b')[0]);
    const italic = !!(rPr && rPr.getElementsByTagNameNS('*','i')[0]);
    const szEl = rPr?.getElementsByTagNameNS('*','sz')[0];
    const szPt = szEl ? (parseInt(getAttr(szEl,'val')||"24",10)/2)|0 : null;
    textRuns.push({txt,bold,italic,szPt});
    combinedTxt += txt;
  }
  combinedTxt = combinedTxt.trim();

  // images inside paragraph
  const drawingEls = pEl.getElementsByTagNameNS('*','drawing');
  const paragraphImages=[];
  for (const dEl of Array.from(drawingEls)) {
    const imgInfo = await parseWordDrawingImage(dEl, relMap, zip);
    if (imgInfo) paragraphImages.push(imgInfo);
  }

  // measure
  pdf.setFont('helvetica', isHeading?'bold':'normal');
  pdf.setFontSize(baseFontSize);
  const prefixW = bulletPrefix ? pdf.getTextWidth(bulletPrefix) : 0;
  const lines = combinedTxt
    ? pdf.splitTextToSize(combinedTxt, contentW - prefixW)
    : [];
  const textNeeded = lines.length ? (lines.length * baseFontSize * 1.2 + 4) : 0;

  // image height
  let imageNeeded = 0;
  for (const img of paragraphImages) {
    const scaled = scaleToFit(img.wPt, img.hPt, contentW, 300); // 300pt max height each
    imageNeeded += scaled.h + 6;
  }

  const needed = Math.max(defaultLineH, textNeeded + imageNeeded);

  // draw closure
  const draw = async (pdf, margin, y, pageW, pageH) => {
    // images first
    for (const img of paragraphImages) {
      const scaled = scaleToFit(img.wPt, img.hPt, contentW, 300);
      const xPt = margin + (contentW - scaled.w)/2;
      await renderDocxImage(pdf, zip, img.mediaPath, xPt, y, scaled.w, scaled.h);
      y += scaled.h + 6;
    }

    // text
    if (combinedTxt) {
      pdf.setFont('helvetica', isHeading?'bold':'normal');
      pdf.setFontSize(baseFontSize);
      let drawX = margin;
      if (bulletPrefix) {
        pdf.text(bulletPrefix, drawX, y);
        drawX += prefixW;
      }
      pdf.text(lines, drawX, y);
      y += textNeeded;
    }
    return y;
  };

  return {needed, draw};
}

/** Simple Word table renderer -> text grid. */
async function renderWordTable(pdf, tblEl, {pageW,pageH,margin,contentW,y}) {
  // Flatten each row's cell text; no borders.
  const rowEls = tblEl.getElementsByTagNameNS('*','tr');
  const rows=[];
  for (const tr of Array.from(rowEls)) {
    const cellEls = tr.getElementsByTagNameNS('*','tc');
    const cells=[];
    for (const tc of Array.from(cellEls)) {
      const text = Array.from(tc.getElementsByTagNameNS('*','t'))
        .map(t=>t.textContent||"")
        .join(" ")
        .trim();
      cells.push(text);
    }
    rows.push(cells);
  }
  if (!rows.length) return y;

  const colCount = Math.max(...rows.map(r=>r.length));
  const colW = contentW / colCount;
  const lineH = 16;

  for (const r of rows) {
    // page break?
    if (y + lineH > pageH - margin) {
      pdf.addPage(); y = margin + lineH;
    }
    r.forEach((cellTxt, idx)=>{
      const x = margin + idx*colW + 2;
      pdf.setFontSize(12);
      pdf.text(pdf.splitTextToSize(cellTxt, colW-4), x, y);
    });
    y += lineH + 2;
  }
  y += 4;
  return y;
}

/** Parse <w:drawing> -> {mediaPath,wPt,hPt} */
async function parseWordDrawingImage(drawingEl, relMap, zip) {
  const picEls = drawingEl.getElementsByTagNameNS('*','pic');
  if (!picEls.length) return null;
  const picEl = picEls[0];
  const blip = picEl.getElementsByTagNameNS('*','blip')[0];
  if (!blip) return null;
  const rid = getAttr(blip,'r:embed') || getAttr(blip,'embed');
  const mediaPath = rid ? relMap[rid] : null;
  if (!mediaPath) return null;

  // extent from wp:inline/anchor
  const inlineEl = drawingEl.getElementsByTagNameNS('*','inline')[0] ||
                   drawingEl.getElementsByTagNameNS('*','anchor')[0];
  let wPt=200,hPt=150;
  if (inlineEl) {
    const extEl = inlineEl.getElementsByTagNameNS('*','extent')[0];
    if (extEl) {
      const cx = parseInt(getAttr(extEl,'cx')||"0",10);
      const cy = parseInt(getAttr(extEl,'cy')||"0",10);
      if (cx&&cy) {wPt=emuToPt(cx);hPt=emuToPt(cy);}
    }
  }
  return {mediaPath,wPt,hPt};
}

/** Scale dims to fit bounding box */
function scaleToFit(w, h, maxW, maxH) {
  const s = Math.min(maxW/w, maxH/h, 1);
  return {w:w*s, h:h*s};
}

/** Render actual image into pdf */
async function renderDocxImage(pdf, zip, mediaPath, x, y, w, h) {
  const imgFile = zip.file(mediaPath);
  if (!imgFile) {
    console.warn(`DOCX missing image: ${mediaPath}`);
    return;
  }
  const uint8 = await imgFile.async("uint8array");
  const ext = mediaPath.split(".").pop()?.toLowerCase() || "png";
  await safeAddImageToPdf({
    pdf, mediaPath, uint8, ext, x, y, w, h
  });
}

/* ---------- Plain text fallback (kept compatible with original signature) ---------- */
const processSimpleText = async (pdf, textContent, margin, contentWidth, pageWidth, pageHeight) => {
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  const lineHeight = 14;
  const lines = pdf.splitTextToSize(textContent, contentWidth);
  let currentY = margin + lineHeight;
  let pageNumber = 1;
  for (let i = 0; i < lines.length; i++) {
    if (currentY + lineHeight > pageHeight - margin) {
      pdf.addPage();
      pageNumber++;
      currentY = margin + lineHeight;
    }
    pdf.text(lines[i], margin, currentY);
    currentY += lineHeight;
  }
  if (pageNumber > 1) {
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - margin - 50,
        pageHeight - 20
      );
    }
  }
};


/** Simple XML parser using DOMParser */
function parseXml(xmlStr) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlStr, 'text/xml');
    const parseError = doc.getElementsByTagName('parsererror')[0];
    if (parseError) {
      console.warn('XML parse error:', parseError.textContent);
      return null;
    }
    return doc;
  } catch (err) {
    console.warn('XML parsing failed:', err);
    return null;
  }
}

/** Get attribute value from element */
function getAttr(el, name) {
  return el.getAttribute(name) || el.getAttributeNS('*', name) || '';
}

/** Resolve relative OOXML target path */
function resolveOOXMLTarget(basePath, target) {
  if (!target) return null;
  let t = target.replace(/\\/g, "/");
  if (t.startsWith("/")) t = t.slice(1);
  if (t.startsWith("word/")) return t;
  const baseParts = basePath.split("/");
  baseParts.pop(); // remove filename
  const targetParts = t.split("/");
  for (const part of targetParts) {
    if (part === "..") baseParts.pop();
    else if (part === ".") continue;
    else baseParts.push(part);
  }
  return baseParts.join("/");
}

/** Convert EMU to points */
function emuToPt(emu) {
  return emu / 12700;
}

/** Safe image addition to PDF with error handling */
async function safeAddImageToPdf({pdf, mediaPath, uint8, ext, x, y, w, h}) {
  try {
    const uint8ToBase64 = (u8) => {
      let binary = "";
      const len = u8.length;
      for (let i = 0; i < len; i++) binary += String.fromCharCode(u8[i]);
      return btoa(binary);
    };

    const guessImageFormatForJsPDF = (ext) => {
      const e = ext.toLowerCase();
      if (e === "jpg" || e === "jpeg") return "JPEG";
      if (e === "png") return "PNG";
      if (e === "gif") return "GIF";
      if (e === "bmp") return "BMP";
      if (e === "webp") return "WEBP";
      return "PNG";
    };

    let dataUrl, jsPdfFormat;

    if (ext === 'svg') {
      // Convert SVG to PNG
      const svgText = new TextDecoder().decode(uint8);
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      dataUrl = await new Promise((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = w;
          canvas.height = h;
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          URL.revokeObjectURL(svgUrl);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          reject(new Error('Failed to load SVG'));
        };
        img.src = svgUrl;
      });
      jsPdfFormat = "PNG";
    } else {
      const base64 = uint8ToBase64(uint8);
      const mime = ext === 'jpg' ? 'jpeg' : ext;
      dataUrl = `data:image/${mime};base64,${base64}`;
      jsPdfFormat = guessImageFormatForJsPDF(ext);
    }

    pdf.addImage(dataUrl, jsPdfFormat, x, y, w, h);
  } catch (error) {
    console.warn(`Failed to add image ${mediaPath}:`, error);
    // Add placeholder text for failed image
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`[Image: ${mediaPath}]`, x, y + 10);
    pdf.setTextColor(0, 0, 0);
  }
}

const extractContentFromFiles = async (files) => {
  let combinedText = "";
  const content = [];

  for (const file of files) {
    let fileText = `--- ${file.name} ---\n`;

    if (file.name.endsWith(".docx")) {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      fileText += result.value + "\n\n";
    }
    else if (file.name.endsWith(".pptx")) {
      const arrayBuffer = await file.arrayBuffer();
      const pptxContent = await extractPptxContent(arrayBuffer);
      fileText += pptxContent + "\n\n";
    }
    else if (file.name.endsWith(".zip")) {
      const zip = await JSZip.loadAsync(file);
      for (const relativePath in zip.files) {
        if (relativePath.endsWith(".docx")) {
          const docxFile = await zip.files[relativePath].async("arraybuffer");
          const result = await mammoth.extractRawText({ arrayBuffer: docxFile });
          fileText += `---- ${relativePath} ----\n${result.value}\n\n`;
        }
        else if (relativePath.endsWith(".pptx")) {
          const pptxFile = await zip.files[relativePath].async("arraybuffer");
          const pptxContent = await extractPptxContent(pptxFile);
          fileText += `---- ${relativePath} ----\n${pptxContent}\n\n`;
        }
      }
    }
    else if (file.name.endsWith(".pdf")) {
      const arrayBuffer = await file.arrayBuffer();
      const pdfContent = await extractPdfContent(arrayBuffer);
      fileText += pdfContent.text + "\n\n";
      content.push(...pdfContent.images);
    } 
    else if (/\.(png|jpeg|jpg|webp|gif)$/i.test(file.name)) {
      const base64Image = await convertImageToBase64(file);
      content.push({
        type: "input_image",
        image_url: `data:image/${file.name.split(".").pop()};base64,${base64Image}`,
      });
    } 
    else {
      // console.log(`Unsupported file type: ${file.name}`);
    }
    combinedText += fileText;
  }

  return { combinedText, content };
};

export const queryRAGFiles = async (question, uploadedFiles) => {
  const ragLambdaUrl = 'https://qy2hu4gwohi2rxjjy2rn4lnzm40knwwa.lambda-url.ap-southeast-1.on.aws/';
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);
    
    const response = await fetch(ragLambdaUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question,
        fileKeys: uploadedFiles.map(f => f.embeddingsKey),
        topK: 5
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('RAG query failed:', errorText);
      throw new Error(`RAG query failed: ${response.status}`);
    }

    const data = await response.json();
    // console.log('rag response', data)
    return data;
  } catch (error) {
    console.error("Error querying RAG files:", error);
    throw error;
  }
};

const getContentType = (filename) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  const contentTypeMap = {
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'zip': 'application/zip',
    'png': 'image/png',
    'jpeg': 'image/jpeg',
    'jpg': 'image/jpeg',
    'webp': 'image/webp',
    'gif': 'image/gif',
    'pdf': 'application/pdf',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  };
  return contentTypeMap[extension] || 'application/octet-stream';
};

export const processFilesRAG = async (files, { setS3Processing, setProcessingFiles, setUploadedFiles, setFileWarningMessage, uploadedFiles }) => {
  const lambdaUrl = 'https://heyogd26exnosx3kbxlwsvjgge0maafb.lambda-url.ap-southeast-1.on.aws/';
  
  setS3Processing(true);
  setProcessingFiles(files.map(f => f.name));

  try {
    const uploadedFileKeys = [];

    // Expand any ZIPs into docx/pptx/pdf Files
    const expandedQueue = [];
    for (const f of files) {
      if (/\.zip$/i.test(f.name)) {
        try {
          const inner = await expandZipToFiles(f);
          if (inner.length === 0) {
            setFileWarningMessage(`⚠️ ZIP "${f.name}" has no supported documents (DOCX/PPTX/PDF).`);
            setTimeout(() => setFileWarningMessage(""), 4000);
            continue;
          }
          expandedQueue.push(...inner);
        } catch (e) {
          console.error('Unzip failed:', e);
          setFileWarningMessage(`❌ Failed to read ZIP "${f.name}": ${e.message}`);
          setTimeout(() => setFileWarningMessage(""), 5000);
        }
      } else {
        expandedQueue.push(f);
      }
    }

    // Show the real files being processed in the UI
    if (expandedQueue.length) {
      setProcessingFiles(expandedQueue.map(f => f.name));
    }

    // Upload + embed expanded items
    for (const file of expandedQueue) {
      const presignedResponse = await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          fileType: getContentType(file.name),
          fileSize: file.size,
          processEmbeddings: false
        })
      });

      if (!presignedResponse.ok) {
        const errorText = await presignedResponse.text();
        throw new Error(`Failed to get upload URL (${presignedResponse.status}): ${errorText.substring(0, 100)}`);
      }

      const { uploadUrl, key } = await presignedResponse.json();

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': getContentType(file.name) }
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload ${file.name} to S3: ${uploadResponse.status}`);
      }

      const embeddingResponse = await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          s3Key: key,
          processEmbeddings: true
        })
      });

      if (!embeddingResponse.ok) {
        const errorText = await embeddingResponse.text();
        throw new Error(`Failed to process embeddings for ${file.name} (${embeddingResponse.status}): ${errorText.substring(0, 100)}`);
      }

      const embeddingResult = await embeddingResponse.json();

      uploadedFileKeys.push({
        filename: file.name,
        s3Key: key,
        embeddingStatus: 'completed',
        chunksCreated: embeddingResult.chunksCreated || 0,
        embeddingsCreated: embeddingResult.embeddingsCreated || 0,
        fileKey: embeddingResult.embeddingsKey,
        embeddingsKey: embeddingResult.embeddingsKey
      });
    }

    const allUploadedFiles = [...uploadedFiles];
    uploadedFileKeys.forEach(newFile => {
      const idx = allUploadedFiles.findIndex(existing => existing.filename === newFile.filename);
      if (idx >= 0) allUploadedFiles[idx] = newFile;
      else allUploadedFiles.push(newFile);
    });

    setUploadedFiles(allUploadedFiles);
    setFileWarningMessage(`Successfully uploaded and processed ${uploadedFileKeys.length} files for RAG! Total files: ${allUploadedFiles.length}`);
    setTimeout(() => setFileWarningMessage(""), 3000);

  } catch (error) {
    console.error("Error uploading files for RAG:", error);
    setFileWarningMessage(`RAG upload failed: ${error.message}`);
    setTimeout(() => setFileWarningMessage(""), 5000);
  } finally {
    setS3Processing(false);
    setProcessingFiles([]);
  }
};


export const isImageFile = (fileName) => {
  const imageExtensions = ['.png', '.jpeg', '.jpg', '.webp', '.gif'];
  return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
};

// ===== ZIP validation config & helpers =====
const ALLOWED_TOP_LEVEL = {
  // What filetypes you allow the *user* to upload at the top level per mode
  rag:     new Set(['docx', 'pptx', 'pdf', 'zip']),
  direct:  new Set(['docx', 'pptx', 'pdf', 'zip', 'png', 'jpeg', 'jpg', 'webp', 'gif']),
  context: new Set(['docx', 'pptx', 'pdf', 'zip', 'png', 'jpeg', 'jpg', 'webp', 'gif']),
};

// What filetypes are allowed *inside* a zip per mode (be strict)
const ALLOWED_IN_ZIP = {
  rag:     new Set(['docx', 'pptx', 'pdf']),
  direct:  new Set(['docx', 'pptx', 'pdf']),
  context: new Set(['docx', 'pptx', 'pdf']), // you only read docx/pptx now; keeping pdf allowed is fine
};

// Always disallow these anywhere inside a zip
const DISALLOWED_ALWAYS = new Set([
  'exe','bat','cmd','sh','js','msi','dll','com','vbs','scr','jar','ps1','php','py','rb','pl',
  'apk','ipa','dylib','so','wasm','7z','rar','tar','gz','bz2','xz'
]);

const getExt = (name='') => {
  const base = name.toLowerCase().split('?')[0].replace(/\\/g,'/').split('/').pop() || '';
  const m = base.match(/\.([^.]+)$/);
  return m ? m[1] : '';
};

const hasPathTraversal = (name='') => {
  const p = name.replace(/\\/g,'/').toLowerCase();
  return p.startsWith('/') || p.includes('../');
};

const isSuspiciousDoubleExt = (name='') => {
  const base = name.toLowerCase().replace(/\\/g,'/').split('/').pop() || '';
  const parts = base.split('.');
  if (parts.length < 3) return false;
  const last = parts[parts.length-1];
  const prev = parts[parts.length-2];
  if (DISALLOWED_ALWAYS.has(last)) return true;
  const commonDocOrImg = new Set(['docx','pptx','pdf','png','jpg','jpeg','gif','webp']);
  return commonDocOrImg.has(prev) && !commonDocOrImg.has(last);
};

/**
 * Validate ZIP content strictly for a given mode.
 * Rejects nested archives, images for RAG, scripts/binaries, path traversal, etc.
 * Returns { ok:boolean, invalidEntries:[{name, reason}] }
 */
const validateZipForMode = async (zipFile, { mode }) => {
  try {
    const zip = await JSZip.loadAsync(zipFile);
    const invalidEntries = [];

    const allowedInside = ALLOWED_IN_ZIP[mode] || new Set();

    for (const [name, entry] of Object.entries(zip.files)) {
      if (entry.dir) continue; // ignore folders

      if (hasPathTraversal(name)) {
        invalidEntries.push({ name, reason: 'Path traversal detected' });
        continue;
      }

      const ext = getExt(name);

      if (ext === 'zip') {
        invalidEntries.push({ name, reason: 'Nested zip not allowed' });
        continue;
      }

      if (DISALLOWED_ALWAYS.has(ext)) {
        invalidEntries.push({ name, reason: `Disallowed file type ".${ext}"` });
        continue;
      }

      if (isSuspiciousDoubleExt(name)) {
        invalidEntries.push({ name, reason: 'Suspicious double extension' });
        continue;
      }

      // Strict allow-list inside ZIP
      if (!allowedInside.has(ext)) {
        invalidEntries.push({
          name,
          reason: `".${ext}" not allowed inside ZIP for ${mode.toUpperCase()} mode`
        });
      }
    }

    return { ok: invalidEntries.length === 0, invalidEntries };
  } catch (e) {
    return { ok: false, invalidEntries: [{ name: zipFile.name, reason: `Unreadable zip: ${e.message}` }] };
  }
};

export const processFilesDirect = async (files, { setS3Processing, setProcessingFiles, setUploadedFiles, setFileWarningMessage, uploadedFiles, setImagePreviews }) => {
  const lambdaUrl = 'https://heyogd26exnosx3kbxlwsvjgge0maafb.lambda-url.ap-southeast-1.on.aws/';
  
  setS3Processing(true); 
  setProcessingFiles(files.map(f => f.name));

  try {
    const uploadedFileIds = [];
    const newImagePreviews = [];

    // Expand any ZIPs first (DOCX/PPTX/PDF only)
    const expandedQueue = [];
    for (const f of files) {
      if (/\.zip$/i.test(f.name)) {
        try {
          const inner = await expandZipToFiles(f);
          if (inner.length === 0) {
            setFileWarningMessage(`⚠️ ZIP "${f.name}" has no supported documents (DOCX/PPTX/PDF).`);
            setTimeout(() => setFileWarningMessage(""), 4000);
            continue;
          }
          expandedQueue.push(...inner);
        } catch (e) {
          console.error('Unzip failed:', e);
          setFileWarningMessage(`❌ Failed to read ZIP "${f.name}": ${e.message}`);
          setTimeout(() => setFileWarningMessage(""), 5000);
        }
      } else {
        expandedQueue.push(f);
      }
    }

    // Reflect the expanded workload in the UI
    if (expandedQueue.length) {
      setProcessingFiles(expandedQueue.map(f => f.name));
    }

    // Process each (images locally; docs via S3 + API; convert docx/pptx→pdf first)
    for (const file of expandedQueue) {
      if (isImageFile(file.name)) {
        setFileWarningMessage(`Processing image: ${file.name}...`);
        try {
          const base64Image = await convertImageToBase64(file);
          const imageFormat = file.name.split('.').pop().toLowerCase();

          newImagePreviews.push({
            name: file.name,
            url: `data:image/${imageFormat};base64,${base64Image}`,
          });

          uploadedFileIds.push({
            filename: file.name,
            type: 'image',
            mode: 'direct',
            status: 'uploaded',
            localImage: true,
            imageFormat,
            base64Image: `data:image/${imageFormat};base64,${base64Image}`
          });

          continue; // skip S3/API for images
        } catch (imgError) {
          console.error(`Error processing image ${file.name}:`, imgError);
          setFileWarningMessage(`⚠️ Failed to process image ${file.name}`);
          setTimeout(() => setFileWarningMessage(""), 3000);
          continue;
        }
      }

      // Non-image: maybe convert, then upload to S3, then send to model API
      let fileToUpload = file;
      let uploadFileName = file.name;
      let conversionType = null;

      if (file.name.toLowerCase().endsWith('.pptx')) {
        setFileWarningMessage(`Converting ${file.name} to PDF (preserving layouts and images)...`);
        try {
          fileToUpload = await convertPptxToPdf(file, false);
          uploadFileName = fileToUpload.name;
          conversionType = 'PPTX';
          setFileWarningMessage(`✅ ${file.name} converted to PDF!`);
          setTimeout(() => {
            setFileWarningMessage(`Processing ${uploadFileName}... (uploading to S3)`);
          }, 2000);
        } catch (e) {
          console.error("PPTX to PDF conversion failed:", e);
          setFileWarningMessage(`⚠️ Failed to convert ${file.name} to PDF, uploading as PPTX`);
        }
      } else if (file.name.toLowerCase().endsWith('.docx')) {
        setFileWarningMessage(`Converting ${file.name} to PDF (preserving text and images)...`);
        try {
          fileToUpload = await convertDocxToPdf(file, false);
          uploadFileName = fileToUpload.name;
          conversionType = 'DOCX';
          setFileWarningMessage(`✅ ${file.name} converted to PDF!`);
          setTimeout(() => {
            setFileWarningMessage(`Processing ${uploadFileName}... (uploading to S3)`);
          }, 2000);
        } catch (e) {
          console.error("DOCX to PDF conversion failed:", e);
          setFileWarningMessage(`⚠️ Failed to convert ${file.name} to PDF, uploading as DOCX`);
        }
      } else {
        setFileWarningMessage(`Processing ${uploadFileName}... (uploading to S3)`);
      }
      
      const presignedResponse = await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: uploadFileName,
          fileType: getContentType(uploadFileName),
          fileSize: fileToUpload.size,
          processEmbeddings: false
        })
      });

      if (!presignedResponse.ok) {
        const errorText = await presignedResponse.text();
        throw new Error(`Failed to get upload URL: ${errorText.substring(0, 100)}`);
      }

      const { uploadUrl, key } = await presignedResponse.json();
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: fileToUpload,
        headers: { 'Content-Type': getContentType(uploadFileName) }
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload ${uploadFileName} to S3`);
      }

      // setFileWarningMessage(`Processing ${uploadFileName}... (uploading to model API)`);
      const processAPI = 'openai';
      const apiFileResponse = await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: uploadFileName,
          s3Key: key,
          processAPI
        })
      });

      if (!apiFileResponse.ok) {
        const errorText = await apiFileResponse.text();
        throw new Error(`Failed to upload to OpenAI: ${errorText.substring(0, 100)}`);
      }

      const fileResult = await apiFileResponse.json();

      uploadedFileIds.push({
        filename: file.name,          // original name for UI
        uploadedAs: uploadFileName,   // actual uploaded file name (maybe .pdf)
        s3Key: key,
        fileId: fileResult.fileId,
        mode: 'direct',
        status: 'uploaded',
        provider: processAPI,
        converted: file.name !== uploadFileName,
        conversionType
      });
    }
    
    // Update image previews
    if (newImagePreviews.length > 0 && typeof setImagePreviews === 'function') {
      setImagePreviews(prev => [...prev, ...newImagePreviews]);
    }
    
    const allUploadedFiles = [...uploadedFiles];
    uploadedFileIds.forEach(newFile => {
      const idx = allUploadedFiles.findIndex(existing => existing.filename === newFile.filename);
      if (idx >= 0) allUploadedFiles[idx] = newFile;
      else allUploadedFiles.push(newFile);
    });

    setUploadedFiles(allUploadedFiles);
    
    const docCount = uploadedFileIds.filter(f => !f.localImage).length;
    const imgCount = uploadedFileIds.filter(f => f.localImage).length;
    const convertedCount = uploadedFileIds.filter(f => f.converted).length;
    const pptxCount = uploadedFileIds.filter(f => f.conversionType === 'PPTX').length;
    const docxCount = uploadedFileIds.filter(f => f.conversionType === 'DOCX').length;
    
    let message = `✅ Successfully processed ${uploadedFileIds.length} files`;
    if (docCount > 0 && imgCount > 0) {
      message += ` (${docCount} documents uploaded to OpenAI, ${imgCount} images processed locally)`;
    } else if (docCount > 0) {
      message += ` (uploaded to OpenAI)`;
    } else if (imgCount > 0) {
      message += ` (processed locally)`;
    }
    if (convertedCount > 0) {
      const conversions = [];
      if (pptxCount > 0) conversions.push(`${pptxCount} PPTX converted to PDF`);
      if (docxCount > 0) conversions.push(`${docxCount} DOCX converted to PDF`);
      message += ` (${conversions.join(', ')})`;
    }
    
    setFileWarningMessage(message);
    setTimeout(() => setFileWarningMessage(""), 3000);

  } catch (error) {
    console.error("Error uploading files for Direct API:", error);
    setFileWarningMessage(`❌ Direct API upload failed: ${error.message}`);
    setTimeout(() => setFileWarningMessage(""), 5000);
  } finally {
    setS3Processing(false);
    setProcessingFiles([]);
  }
};


export const processFilesContext = async (files, { fileList, setFileList, setExtractedText, setFileWarningMessage }) => {
  const newFileList = [...fileList];
  const filesToProcess = [];
  let warning = "";

  const supportedFileTypes = [".docx", ".zip", ".png", ".jpeg", ".jpg", ".webp", ".gif", ".pdf", ".pptx"];

  for (const file of files) {
    if (
      supportedFileTypes.some((type) => file.name.endsWith(type)) &&
      !newFileList.some((f) => f.name === file.name)
    ) {
      newFileList.push(file);
      filesToProcess.push(file);
    } else {
      warning = `${file.name} is not a supported file type or is already added.`;
    }
  }

  if (warning) {
    setFileWarningMessage(warning);
    setTimeout(() => setFileWarningMessage(""), 5000);
    return;
  }

  if (filesToProcess.length === 0) {
    return;
  }

  const { combinedText } = await extractContentFromFiles(filesToProcess);
  
  setFileList(newFileList);
  setExtractedText((prevText) => prevText + combinedText);
};



const FileUpload = ({ 
  isOpen, 
  onClose, 
  uploadMode, 
  setUploadMode, 
  setIsS3UploadEnabled,
  fileList, 
  setFileList,
  fileWarningMessage, 
  setFileWarningMessage,
  uploadedFiles,
  setUploadedFiles,
  extractedText,
  setExtractedText,
  imagePreviews,
  setImagePreviews,
  isDirectUploadModelSelected = false // New prop to detect if 4o/4o-mini/5/5-mini/5-nano is selected
}) => {
  const [isProcessingUpload, setS3Processing] = React.useState(false);
  const [processingFiles, setProcessingFiles] = React.useState([]);

  const [fileProgress, setFileProgress] = React.useState({});

  // once, when fileList changes
  const progressMetaRef = React.useRef({}); // name -> { startedAt, startDelayMs, baseStep, jitter, cap }

  function ensureMeta(file){
    if (progressMetaRef.current[file.name]) return;

    const mb = Math.max(0.01, file.size / (1024*1024));
    const baseStep = Math.min(1.8, Math.max(0.35, 1.6 / (1 + Math.log1p(mb))));
    const jitter    = 0.4 + Math.random() * 0.6;      // 0.4–1.0
    const cap       = 92 + Math.floor(Math.random()*6);// 92–97%
    const startDelayMs = 150 + Math.random()*650;      // 0.15–0.8s stagger
    const startedAt = Date.now() + startDelayMs;

    progressMetaRef.current[file.name] = { mb, baseStep, jitter, cap, startDelayMs, startedAt };
  }

  React.useEffect(() => {
    fileList.forEach(ensureMeta);
    Object.keys(progressMetaRef.current).forEach(n=>{
      if(!fileList.some(f=>f.name===n)) delete progressMetaRef.current[n];
    });
  }, [fileList]);


  // init/cleanup meta when fileList changes
  React.useEffect(() => {
    const meta = progressMetaRef.current;

    fileList.forEach((file) => {
      const name = file.name;
      if (!meta[name]) {
        const sizeMB = Math.max(0.1, (file.size || 0) / (1024 * 1024));

        // map size to a base step per tick:
        // tiny files ~2.2%/tick → big files (20MB+) ~0.6%/tick
        const t = Math.min(1, Math.max(0, (sizeMB - 0.1) / 20));
        const baseStep = 2.2 * (1 - t) + 0.6 * t;

        // per-file random jitter so multiple files feel different
        const jitter = 0.4 + Math.random() * 0.6; // 0.4–1.0

        meta[name] = { baseStep, jitter, cap: 95 }; // keep moving until 95 while processing
      }
    });

    // remove stale meta for files no longer present
    Object.keys(meta).forEach((name) => {
      if (!fileList.some((f) => f.name === name)) delete meta[name];
    });
  }, [fileList]);


  React.useEffect(() => {
  if (!fileList.length) return;

  const id = setInterval(() => {
    setFileProgress(prev => {
      const next = { ...prev };

      fileList.forEach(file => {
        const name = file.name;
        const meta = progressMetaRef.current[name] || {};
        const { baseStep = 1.0, jitter = 0.6, cap = 95 } = meta;

        const uploadedFile    = uploadedFiles.find(u => u.filename === name);
        const embeddingStatus = uploadedFile?.embeddingStatus;
        const directStatus    = uploadedFile?.status;

        const isProcessing = processingFiles.includes(name);
        const hasCompleteStatus =
          embeddingStatus === 'completed' ||
          embeddingStatus === 'failed'    ||
          directStatus === 'uploaded'     ||
          directStatus === 'failed';

        // Only “done” when not processing AND backend says finished
        const isDone = !isProcessing && hasCompleteStatus;

        const current = next[name] ?? 0;

        if (!isDone) {
          // random, size-aware drift toward cap
          let noisy = Math.max(0.15, baseStep + (Math.random() - 0.5) * jitter);
          if (Math.random() < 0.06) noisy *= 0.25; // tiny stall
          if (Math.random() < 0.04) noisy *= 1.8;  // small surge

          const ease =
            current < 30 ? 1.0 :
            current < 60 ? 0.8 :
            current < 80 ? 0.6 : 0.45;

          next[name] = Math.min(cap, current + noisy * ease);
        } else if (current < 100) {
          // completion: quickly finish ring (still scaled by size a bit)
          const finishStep = Math.max(3.0, baseStep * 12);
          next[name] = Math.min(100, current + finishStep);
        }
      });

      // cleanup for removed files
      Object.keys(next).forEach(name => {
        if (!fileList.some(f => f.name === name)) delete next[name];
      });

      return next;
    });
  }, 100);

  return () => clearInterval(id);
}, [fileList, uploadedFiles, processingFiles]);






  if (!isOpen) return null;

  // Internal processing functions that use local state
  const processFilesContextInternal = async (files) => {
    const newFileList = [...fileList];
    const filesToProcess = [];
    let warning = "";

    const supportedFileTypes = [".docx", ".zip", ".png", ".jpeg", ".jpg", ".webp", ".gif", ".pdf", ".pptx"];

    for (const file of files) {
      if (
        supportedFileTypes.some((type) => file.name.endsWith(type)) &&
        !newFileList.some((f) => f.name === file.name)
      ) {
        newFileList.push(file);
        filesToProcess.push(file);
      } else {
        warning = `${file.name} is not a supported file type or is already added.`;
      }
    }

    if (warning) {
      setFileWarningMessage(warning);
      setTimeout(() => setFileWarningMessage(""), 5000);
      return;
    }

    if (filesToProcess.length === 0) {
      return;
    }

    const { combinedText } = await extractContentFromFiles(filesToProcess);
    
    setFileList(newFileList);
    setExtractedText((prevText) => prevText + combinedText);
  };

  const ALLOWED_DESC = {
    rag:     'DOCX, PPTX, PDF, ZIP (no images)',
    direct:  'DOCX, PPTX, PDF, ZIP, PNG, JPEG, JPG, WEBP, GIF',
    context: 'DOCX, PPTX, PDF, ZIP, PNG, JPEG, JPG, WEBP, GIF'
  };

  function formatRejectionMessage(rejections = [], mode = 'context') {
    if (!rejections.length) return '';

    const lines = ['❌ Some files could not be added:'];
    const LIST_CAP = 5;
    const REASON_CAP = 3;

    rejections.slice(0, LIST_CAP).forEach(({ name, reasons = [] }) => {
      lines.push(`• ${name}`);
      reasons.slice(0, REASON_CAP).forEach(r => lines.push(`   - ${r}`));
      if (reasons.length > REASON_CAP) {
        lines.push(`   - ...and ${reasons.length - REASON_CAP} more issue(s)`);
      }
    });

    if (rejections.length > LIST_CAP) {
      lines.push(`…and ${rejections.length - LIST_CAP} more file(s).`);
    }

    const allowedMode = mode === 'context' ? 'context' : mode; // keep wording consistent
    lines.push('');
    lines.push(`What files are allowed: ${ALLOWED_DESC[allowedMode]}`);

    return lines.join('\n');
  }

  const filterFilesForMode = async (files, mode, { isDirectUploadModelSelected }) => {
    const filteredFiles = [];
    const rejections = [];

    // pick the top-level allow list based on mode
    const topLevelMode = (mode === 'context' && isDirectUploadModelSelected) ? 'direct' : mode;

    for (const file of files) {
      const ext = getExt(file.name);

      // RAG: reject images outright at top level
      if (mode === 'rag' && isImageFile(file.name)) {
        rejections.push({
          name: file.name,
          reasons: ['Images are not supported in RAG mode. Please upload DOCX, PPTX, PDF, or ZIP instead.']
        });
        continue;
      }

      // Top-level allow list check
      const allowedTop = ALLOWED_TOP_LEVEL[topLevelMode] || new Set();
      if (!allowedTop.has(ext)) {
        rejections.push({
          name: file.name,
          reasons: [`".${ext}" files are not allowed in ${topLevelMode.toUpperCase()} mode.`]
        });
        continue;
      }

      // Extra scrutiny for zips
      if (ext === 'zip') {
        const { ok, invalidEntries } = await validateZipForMode(file, { mode: topLevelMode });
        if (!ok) {
          const details = invalidEntries.slice(0, 5).map(e => `${e.name}: ${e.reason}`);
          if (invalidEntries.length > 5) {
            details.push(`...and ${invalidEntries.length - 5} more item(s)`);
          }
          rejections.push({
            name: file.name,
            reasons: [
              'ZIP contains unsupported or unsafe items:',
              ...details
            ]
          });
          continue;
        }
      }

      filteredFiles.push(file);
    }

    return { filteredFiles, rejections, topLevelMode };
  };


  const processFiles = async (files) => {
    const { filteredFiles, rejections, topLevelMode } = await filterFilesForMode(
      files,
      uploadMode,
      { isDirectUploadModelSelected }
    );

    // Log the current upload mode and model status
    // console.log(`File Upload Mode: ${uploadMode} | Direct Upload Model: ${isDirectUploadModelSelected ? 'Yes' : 'No'}`);

    // Show rejections (with reasons)
    if (rejections.length > 0) {
      const msg = formatRejectionMessage(rejections, topLevelMode);
      setFileWarningMessage(msg);
      setTimeout(() => setFileWarningMessage(""), 7000);

      // If no valid files remain, return early
      if (filteredFiles.length === 0) return;
    }

    const stateSetters = {
      setS3Processing,
      setProcessingFiles,
      setUploadedFiles,
      setFileWarningMessage,
      uploadedFiles,
      fileList,
      setFileList,
      setExtractedText,
      setImagePreviews
    };

    switch (uploadMode) {
      case 'rag':
        await processFilesRAG(filteredFiles, stateSetters);
        if (typeof setIsS3UploadEnabled === 'function') setIsS3UploadEnabled(true);
        break;
      case 'context':
      default:
        if (isDirectUploadModelSelected) {
          setFileWarningMessage(`📄 GPT-4o/4o-mini detected - processing files for direct API upload...`);
          setTimeout(() => setFileWarningMessage(""), 5000);
          await processFilesDirect(filteredFiles, stateSetters);
          if (typeof setIsS3UploadEnabled === 'function') setIsS3UploadEnabled(false);
        } else {
          await processFilesContextInternal(filteredFiles);
          if (typeof setIsS3UploadEnabled === 'function') setIsS3UploadEnabled(false);
        }
        break;
    }
  };

  



  const handleFileChange = async (e) => {
    const picked = Array.from(e.target.files);

    const { filteredFiles, rejections, topLevelMode } = await filterFilesForMode(
      picked,
      uploadMode,
      { isDirectUploadModelSelected }
    );

    if (rejections.length > 0) {
      const msg = formatRejectionMessage(rejections, topLevelMode);
      setFileWarningMessage(msg);
      setTimeout(() => setFileWarningMessage(""), 7000);
    }

    if (filteredFiles.length > 0) {
      // Only count files that are not already in fileList
      const uniqueNewFiles = filteredFiles.filter(
        f => !fileList.some(existing => existing.name === f.name)
      );

      const currentTotalBytes = fileList.reduce(
        (sum, f) => sum + (f.size || 0),
        0
      );
      const newBytes = uniqueNewFiles.reduce(
        (sum, f) => sum + (f.size || 0),
        0
      );

      if (currentTotalBytes + newBytes > MAX_TOTAL_UPLOAD_BYTES) {
        setFileWarningMessage(
          '❌ Total file size cannot exceed 32 MB. Please remove some files or upload fewer / smaller files.'
        );
        setTimeout(() => setFileWarningMessage(""), 5000);
        // allow re-selecting same file next time
        e.target.value = '';
        return;
      }

      const letProcessAdd = (uploadMode === 'context' && !isDirectUploadModelSelected);
      if (!letProcessAdd) {
        setFileList(prev => mergeWithoutDupes(prev, filteredFiles));
      }

      setFileProgress(prev => {
        const next = { ...prev };
        filteredFiles.forEach(f => {
          next[f.name] = 0;
        });
        return next;
      });

      if (uploadMode === 'context' && !isDirectUploadModelSelected) {
        const supportedImageTypes = [".png", ".jpeg", ".jpg", ".webp", ".gif"];
        const toPreview = filteredFiles.filter(f => supportedImageTypes.some(t => f.name.endsWith(t)));
        if (toPreview.length) {
          const previews = [];
          for (const f of toPreview) {
            const base64Image = await convertImageToBase64(f);
            previews.push({ name: f.name, url: `data:image/${f.name.split('.').pop()};base64,${base64Image}` });
          }
          setImagePreviews(prev => [...prev, ...previews]);
        }
      }

      await processFiles(filteredFiles);
    }


    // allow re-selecting the same file after rejection
    e.target.value = '';
  };


  const handleClose = () => {
    if (isProcessingUpload) {
      setFileWarningMessage("⚠️ Cannot close while files are being processed. Please wait...");
      setTimeout(() => setFileWarningMessage(""), 3000);
      return;
    }
    onClose();
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();

    const dropped = Array.from(e.dataTransfer.files);

    // Block ZIP files, comment this out if need to restore functionality
    // const rejectedZips = dropped.filter(f => /\.zip$/i.test(f.name));
    // const files = dropped.filter(f => !/\.zip$/i.test(f.name));

    // if (rejectedZips.length) {
    //   setFileWarningMessage(
    //     `❌ ZIP files are not supported: ${rejectedZips.map(f => f.name).join(', ')}`
    //   );
    //   setTimeout(() => setFileWarningMessage(""), 5000);
    // }

    // if (files.length === 0) return;
    const files = dropped;


    const supportedImageTypes = [".png", ".jpeg", ".jpg", ".webp", ".gif"];
    const newPreviews = [];

    if (uploadMode === 'context' && !isDirectUploadModelSelected) {
      for (const file of files) {
        if (supportedImageTypes.some((type) => file.name.toLowerCase().endsWith(type))) {
          const base64Image = await convertImageToBase64(file);
          newPreviews.push({
            name: file.name,
            url: `data:image/${file.name.split(".").pop()};base64,${base64Image}`,
          });
        }
      }
      if (newPreviews.length) {
        setImagePreviews((prev) => [...prev, ...newPreviews]);
      }
    }

    const newFileList = [...fileList];
    const filesToAdd = [];

    for (const file of files) {
      if (!newFileList.some(f => f.name === file.name)) {
        newFileList.push(file);
        filesToAdd.push(file);
      }
    }

    setFileList(newFileList);

    if (filesToAdd.length > 0) {

      // 🔹 reset progress for all files being (re)processed
      setFileProgress(prev => {
        const next = { ...prev };
        filesToAdd.forEach(f => {
          next[f.name] = 0;
        });
        return next;
      });

      await processFiles(filesToAdd);
    }
  };


  const removeFile = async (fileName) => {
    if (uploadMode === 'rag') {
      const updatedUploadedFiles = uploadedFiles.filter(file => file.filename !== fileName);
      const updatedFileList = fileList.filter((file) => file.name !== fileName);
      const updatedImagePreviews = imagePreviews.filter(
        (preview) => preview.name !== fileName
      );

      setUploadedFiles(updatedUploadedFiles);
      setFileList(updatedFileList);
      setImagePreviews(updatedImagePreviews); 
      
      setFileWarningMessage(`Removed ${fileName} from files`);
      setTimeout(() => setFileWarningMessage(""), 2000);
    } else {
      // Context mode (with or without direct upload)
      const updatedFileList = fileList.filter((file) => file.name !== fileName);
      const updatedImagePreviews = imagePreviews.filter(
        (preview) => preview.name !== fileName
      );
      
      // Also remove from uploadedFiles if it was directly uploaded
      if (uploadedFiles.some(file => file.filename === fileName)) {
        setUploadedFiles(uploadedFiles.filter(file => file.filename !== fileName));
      }

      const { combinedText } = await extractContentFromFiles(updatedFileList);

      setFileList(updatedFileList);
      setImagePreviews(updatedImagePreviews);
      setExtractedText(combinedText);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return '🖼️';
    if (ext === 'docx') return '📄';
    if (ext === 'pdf') return '📋';
    if (ext === 'pptx') return '📊';
    if (ext === 'zip') return '📦';
    return '📁';
  };
    
  return (
    <div
      className="files-upload-popup"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleFileDrop}
    >
      <div className="files-popup-content">

      <button
        className="close-button close-button--abs"
        onClick={handleClose}
        aria-label="Close upload dialog"
        title={isProcessingUpload ? 'Cannot close while processing files' : 'Close'}
        aria-disabled={isProcessingUpload}     // visual state only; still clickable for the warning
      >
        <X size={18} />
      </button>
        <div className="upload-mode-header">
          
        {fileWarningMessage && (
          <div className={`file-warning ${isProcessingUpload ? 'processing' : ''}`}>
            <span>{isProcessingUpload ? '⏳' : ''}</span>
            <p>{fileWarningMessage}</p>
          </div>
        )}

          <h2>Upload Files</h2>
          <div className="upload-mode-controls">
            <div className="upload-mode-dropdown">
              <label htmlFor="upload-mode-select">Mode:</label>
              <select
                id="upload-mode-select"
                value={uploadMode}
                onChange={(e) => {
                  if (isProcessingUpload) {
                    setFileWarningMessage("⚠️ Cannot change mode while processing files");
                    setTimeout(() => setFileWarningMessage(""), 3000);
                    return;
                  }
                  
                  // Clear image previews when switching away from context mode
                  if (uploadMode === 'context' && e.target.value !== 'context') {
                    setImagePreviews([]);
                  }
                  
                  setUploadMode(e.target.value);
                }}
                className="upload-mode-select"
                disabled={isProcessingUpload}
              >
                <option value="context">📝 Context</option>
                <option value="rag">📚 Strict Text (RAG)</option>
              </select>
            </div>
          </div>
        </div>

        {fileList.length > 0 && (
          <div className="files-section">
            <div className="file-tabs">
              {fileList.map((file) => {
                const uploadedFile = uploadedFiles.find(u => u.filename === file.name);
                const embeddingStatus = uploadedFile?.embeddingStatus;
                const directStatus = uploadedFile?.status;
                const isCurrentlyProcessing = processingFiles.includes(file.name);
                const progress = fileProgress[file.name] ?? 0;
                
                return (
                  <div className="file-tab" key={file.name}> {/* ← stable key */}
                    <span className="file-icon">
                      {(progress < 100 && isDirectUploadModelSelected === true)? (
                        <div className="relative h-6 w-6">
                          <div
                            className="absolute inset-0 rounded-full"
                            style={{
                              background: `conic-gradient(#ffffff ${Math.max(progress, 2) * 3.6}deg, rgba(255,255,255,0.16) 0deg)`,
                            }}
                          />
                          <div className="absolute inset-[2.5px] rounded-full bg-[#3b3b3b]" />
                        </div>
                      ) : (
                        getFileIcon(file.name)
                      )}
                    </span>


                    <span className="file-name">{file.name}</span>

                    {uploadMode === 'rag' && (
                      <span className="file-status">
                        {isCurrentlyProcessing && '⏳'}
                        {!isCurrentlyProcessing && embeddingStatus === 'completed' && '✅'}
                        {!isCurrentlyProcessing && embeddingStatus === 'failed' && '❌'}
                      </span>
                    )}

                    {uploadMode === 'context' && isDirectUploadModelSelected && directStatus && (
                      <span className="file-status">
                        {isCurrentlyProcessing && '⏳'}
                        {!isCurrentlyProcessing && directStatus === 'uploaded' && '✅'}
                        {!isCurrentlyProcessing && directStatus === 'failed' && '❌'}
                      </span>
                    )}

                    <button
                      className="remove-file"
                      onClick={() => {
                        // 1) drop any “in-progress” marker for this file so the status icon disappears immediately
                        if (processingFiles.includes(file.name)) {
                          setProcessingFiles(prev => prev.filter(n => n !== file.name));
                        }
                        // 2) call your existing remove
                        removeFile(file.name);
                      }}
                      disabled={isProcessingUpload}
                      aria-label={`Remove ${file.name}`}
                      title={`Remove ${file.name}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        <div className={`container-dropzone ${uploadMode} ${isProcessingUpload ? 'processing' : ''}`}>
          <input
            type="file"
            id="file-input"
            multiple
            // accept=".docx, .png, .jpeg, .jpg, .webp, .gif, .pdf, .pptx"
            accept=".docx, .zip, .png, .jpeg, .jpg, .webp, .gif, .pdf, .pptx"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={isProcessingUpload}
          />
          <div 
            className="file-dropzone"
            onClick={() => {
              if (!isProcessingUpload) {
                document.getElementById("file-input").click();
              }
            }}
            style={{ 
              opacity: isProcessingUpload ? 0.6 : 1, 
              cursor: isProcessingUpload ? 'not-allowed' : 'pointer' 
            }}
          >
            <FilePlus size={32} className="file-icon" />
              <span className="text-neutral-200 font-medium">
                {isProcessingUpload
                  ? "Uploading & processing file(s)…"
                  : "Drag & drop or click to upload"}
              </span>

            
            <div className="file-requirements">
            {uploadMode === 'context' && (
              <ul className="file-disclaimers">
                <li><strong>What it does:</strong> The AI reads your document text and helps answer questions about it</li>
                {/* <li><strong>Supported documents:</strong> DOCX, PPTX, PDF</li> */}
                <li><strong>Supported documents:</strong> DOCX, PPTX, PDF, ZIP</li>
                <li><strong>Image support:</strong> Only GPT-4o/4o-mini models can "see" images (PNG, JPEG, JPG, WEBP, GIF)</li>
                <li><strong>Important:</strong> Images embedded within documents are only visible to GPT-4o/4o-mini models</li>
                <li><strong>Best for:</strong> Quick answers about your documents, and viewing images with GPT-4o/4o-mini</li>
              </ul>
            )}
            
            {uploadMode === 'rag' && (
              <ul className="file-disclaimers">
                <li><strong>What it does:</strong> Searches your documents for relevant information before answering</li>
                {/* <li><strong>Supported:</strong> Text documents only (DOCX, PPTX, PDF), doesnt "read" images in the files.</li> */}
                <li><strong>Supported:</strong> Text documents only (DOCX, PPTX, PDF, ZIP), doesnt "read" images in the files.</li>
                <li><strong>Best for:</strong> Large documents or when you need precise information with sources</li>
                <li><strong>Similarity scores:</strong> Shows percentage match between your question and document chunks</li>
              </ul>
            )}
          </div>

           
          </div>
        </div>

        <div className="popup-actions">
          {/* <button 
            className={`btn-secondary ${isProcessingUpload ? 'disabled' : ''}`}
            onClick={handleClose}
            disabled={isProcessingUpload}
            title={isProcessingUpload ? 'Cannot close while processing files' : 'Close'}
          >
            {isProcessingUpload ? 'Processing...' : 'Close'}
          </button> */}
        </div>
      </div>
    </div>
  );
};

export {
  convertImageToBase64,
  extractContentFromFiles,
  promisifiedPDFGetObject,
  extractPptxContent,
  extractPdfContent,
};

export default FileUpload;